import { useEffect, useState, useCallback } from "react";
import { trpc } from "./trpc";
import { derivePushStatus, type PushStatus, type PushPermission } from "../../../shared/push-status";

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const buf = new ArrayBuffer(rawData.length);
  const output = new Uint8Array(buf);
  for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i);
  return output;
}

export interface PushSubscriptionState {
  status: PushStatus;
  /** 失敗原因（三語由呼叫端決定；這裡放技術訊息供顯示） */
  error: string | null;
  /** 剛完成啟用／停用，供 UI 顯示短暫的成功提示 */
  justChanged: "enabled" | "disabled" | null;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
}

/**
 * 瀏覽器推播訂閱。
 * 2026-08-29：原本失敗（權限被拒、無 VAPID 金鑰、訂閱例外）全都靜默 return，
 * 使用者按了看不出任何變化 —— 改為對外吐出明確狀態與錯誤。
 */
export function usePushSubscription(): PushSubscriptionState {
  const supported =
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window;

  const [permission, setPermission] = useState<PushPermission>(
    supported ? Notification.permission : "unsupported",
  );
  const [isSubscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justChanged, setJustChanged] = useState<"enabled" | "disabled" | null>(null);

  const vapidQ = trpc.notification.vapidPublicKey.useQuery();
  const subscribeMut = trpc.notification.subscribe.useMutation();
  const unsubscribeMut = trpc.notification.unsubscribe.useMutation();

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.getRegistration().then(async (reg) => {
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    });
  }, [supported]);

  // 成功提示 4 秒後自動收起
  useEffect(() => {
    if (!justChanged) return;
    const timer = window.setTimeout(() => setJustChanged(null), 4000);
    return () => window.clearTimeout(timer);
  }, [justChanged]);

  const subscribe = useCallback(async () => {
    if (!supported || busy) return;
    setError(null);
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        // 使用者按了「封鎖」或關掉提示 —— 狀態會轉為 denied/idle，UI 據此說明
        return;
      }
      const key = vapidQ.data?.key;
      if (!key) throw new Error("伺服器尚未設定推播金鑰（VAPID）");

      const reg = await navigator.serviceWorker.register("/sw.js");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
      const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
      await subscribeMut.mutateAsync({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        userAgent: navigator.userAgent,
      });
      setSubscribed(true);
      setJustChanged("enabled");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [supported, busy, vapidQ.data?.key, subscribeMut]);

  const unsubscribe = useCallback(async () => {
    if (!supported || busy) return;
    setError(null);
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await unsubscribeMut.mutateAsync({ endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      setJustChanged("disabled");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [supported, busy, unsubscribeMut]);

  const status = derivePushStatus({
    supported,
    hasVapidKey: !!vapidQ.data?.key,
    permission,
    isSubscribed,
    busy,
  });

  return { status, error, justChanged, subscribe, unsubscribe };
}
